import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  WritableSignal,
  signal,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CrudOperation } from '@shared/models';
import { AppNotificationService } from '@shared/services';
import { QueryOptions } from 'odata-query';
import { TaxResultService } from 'src/app/modules/tax-result/services/tax-result.service';
import {
  Collection,
  GridItemCreateEvent,
  GridItemUpdateEvent,
  SecurityType,
} from '../../models';
import { SecurityTypeService } from '../../services';

@Component({
  standalone: false,
  selector: 'gstar-security-page-page',
  templateUrl: 'security-types-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SecurityTypesPageComponent implements OnInit {
  private readonly securityTypeService = inject(SecurityTypeService);
  private readonly appNotificationService = inject(AppNotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly taxResultService = inject(TaxResultService);

  public securityTypes: WritableSignal<Collection<SecurityType>> = signal({
    total: 0,
    items: [],
  });

  public query: Partial<QueryOptions<SecurityType>> = {
    orderBy: 'isParent desc',
    count: true,
    skip: 0,
    top: 10,
  };

  private securityTypeName = this.translateService.instant(
    'admin.dashboard.securityTypesTitle'
  );

  ngOnInit(): void {
    this.fetchSecurityTypes();
  }

  create(securityTypeQuery: GridItemCreateEvent<SecurityType>): void {
    let isSuccess = false;
    this.securityTypeService.create(securityTypeQuery.item).subscribe(() => {
      securityTypeQuery.onSuccess();
      isSuccess = true;

      this.securityTypeService.clearCache();
      this.taxResultService.updateSecurities();
      this.fetchSecurityTypes();

      this.appNotificationService.crudNotify(
        CrudOperation.Create,
        this.securityTypeName,
        isSuccess
      );
    });
  }

  update(securityTypeQuery: GridItemUpdateEvent<SecurityType>): void {
    let isSuccess = false;
    this.securityTypeService.update(securityTypeQuery.item).subscribe(() => {
      securityTypeQuery.onSuccess();
      isSuccess = true;

      this.securityTypeService.clearCache();
      this.taxResultService.updateSecurities();
      this.fetchSecurityTypes();

      this.appNotificationService.crudNotify(
        CrudOperation.Update,
        this.securityTypeName,
        isSuccess
      );
    });
  }

  queryUpdate(securityTypeQuery: Partial<QueryOptions<SecurityType>>): void {
    this.query = securityTypeQuery;
    this.fetchSecurityTypes();
  }

  private fetchSecurityTypes(): void {
    this.securityTypeService
      .get({ ...this.query, count: true })
      .subscribe((securityTypeCollection: Collection<SecurityType>) => {
        this.securityTypes.set(securityTypeCollection);
      });
  }
}
